import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
    BASE_URL_TOKEN,
    ConfigBundle,
    ConfigBundleCommitRequest,
    ConfigBundleCommitResult,
    ConfigBundlePreviewRequest,
    ConfigBundlePreviewResult,
    ConfigTransferRecord,
} from '@raptor-fe/core';

/**
 * Calls the tenant configuration transfer endpoints (export, preview, commit, history). Every route is
 * restricted server-side to Entra staff admins holding `tenant:configtransfer` and is switched off per
 * environment, so a 403 or 404 here is an expected outcome rather than a client bug.
 */
@Injectable({
    providedIn: 'root',
})
export class TenantConfigTransferService {
    protected readonly http = inject(HttpClient);
    protected readonly baseUrl = inject(BASE_URL_TOKEN);

    /**
     * Fetches the tenant's configuration bundle. The full response is observed because the bundle hash and
     * the server-chosen download file name travel in the `X-Bundle-Sha256` and `Content-Disposition` headers.
     */
    exportBundle(tenantId: string, includeFeatureFlags: boolean): Observable<HttpResponse<ConfigBundle>> {
        const params = new HttpParams().set('includeFeatureFlags', includeFeatureFlags);
        return this.http.get<ConfigBundle>(this.bundleUrl(tenantId), { params, observe: 'response', responseType: 'json' });
    }

    /** Computes what importing the bundle would do. Writes nothing. */
    preview(tenantId: string, request: ConfigBundlePreviewRequest): Observable<ConfigBundlePreviewResult> {
        return this.http.post<ConfigBundlePreviewResult>(`${this.bundleUrl(tenantId)}/preview`, request);
    }

    /** Applies a previewed bundle in one server-side transaction. */
    commit(tenantId: string, request: ConfigBundleCommitRequest): Observable<ConfigBundleCommitResult> {
        return this.http.post<ConfigBundleCommitResult>(`${this.bundleUrl(tenantId)}/commit`, request);
    }

    /** Returns the tenant's most recent exports and committed imports, newest first. */
    getHistory(tenantId: string): Observable<ConfigTransferRecord[]> {
        return this.http.get<ConfigTransferRecord[]>(`${this.bundleUrl(tenantId)}/history`);
    }

    private bundleUrl(tenantId: string): string {
        return `${this.baseUrl}v1/tenant/${encodeURIComponent(tenantId)}/config-bundle`;
    }
}
